/* eslint-disable consistent-return */

import { useEffect, useRef } from 'react'

import { getSelectorsByUserAgent, isMobile } from 'react-device-detect'

import { AdType } from '@web/components/Advertisement/Advertisement'
import { AdSlot, GladSdk, AdChannel, GladSdkListener, GladSdkEvent } from '@web/components/Advertisement/types'
import { naverAppearanceVar } from '@web/hooks/useGlobalDetermineDarkMode'
import configs from '@web/libs/constants/config'
import { isFromMapsApp } from '@web/libs/entry.helper'
import { ValueOf } from '@web/types'

const DEDUPE_MANAGER_ID = 'myplaceDedupeManagerId'
/**
 * 중복없이 노출할 개수 e.g. 5 => n번째 광고는 앞의 4개와 중복되지 않는 광고를 가짐
 */
const DEDUPE_SLOT_COUNT = 6

export const initDedupeManager = (gladSdk: GladSdk) => {
  const existManager = gladSdk.findAdDedupManager(DEDUPE_MANAGER_ID)
  if (existManager) return existManager

  return gladSdk.setAdDeduplication(DEDUPE_MANAGER_ID, DEDUPE_SLOT_COUNT - 1)
}

export const initAdSdk = () => {
  if (window.gladsdk) return
  window.gladsdk = { cmd: [] } as unknown as GladSdk
}

const getChannel = (): AdChannel => {
  const userAgent = window.navigator.userAgent

  const isMapApp = isFromMapsApp(userAgent)
  if (!isMapApp) return AdChannel.WEB

  const { isAndroid, isIOS } = getSelectorsByUserAgent(userAgent)
  if (isAndroid) return AdChannel.MAP_AOS

  if (isIOS) return AdChannel.MAP_IOS

  return AdChannel.WEB
}

export const initAd = ({
  adSlotElementIds,
  adUnitId,
  customParam,
  dedupe = false,
}: {
  adSlotElementIds: string[]
  adUnitId: string
  customParam?: Record<string, string | undefined>
  dedupe?: boolean
}) => {
  const gladSdk = window?.gladsdk
  if (!gladSdk) return

  gladSdk.cmd.push(() => {
    const gladSdk = window?.gladsdk
    if (!gladSdk) return

    const newAdSlots = adSlotElementIds
      .map(adSlotElementId => {
        const existAdSlot = gladSdk.findAdSlot(adSlotElementId)
        if (existAdSlot) return

        const newAdSlot = gladSdk.defineAdSlot({
          adSlotElementId: adSlotElementId,
          adUnitId,
          customParam: {
            channel: getChannel(),
            ...customParam,
          },
        })

        return newAdSlot
      })
      .filter(Boolean) as AdSlot[]

    const naverAppearance = naverAppearanceVar()

    gladSdk.setHostMeta('theme', naverAppearance)

    gladSdk.setGlobalConfig({
      ...configs.gfpAdCallParam.config,
    })

    if (newAdSlots.length === 0) return

    if (!dedupe) {
      gladSdk.displayAd(newAdSlots[0])
      return
    }

    const dedupeManager = initDedupeManager(gladSdk)
    dedupeManager.displayAds(newAdSlots)
  })
}

export const cleanUpAd = (adSlotElemntId: string) => {
  const gladSdk = window?.gladsdk
  if (!gladSdk) return

  gladSdk.cmd.push(() => {
    const gladSdk = window?.gladsdk
    if (!gladSdk) return

    const adSlot = gladSdk.findAdSlot?.(adSlotElemntId)
    if (!adSlot) return

    gladSdk.destroyAdSlots([adSlot])
  })
}

export const cleanUpAllAds = () => {
  const gladSdk = window?.gladsdk
  if (!gladSdk) return

  gladSdk.cmd.push(() => {
    const gladSdk = window?.gladsdk
    if (!gladSdk) return

    gladSdk.removeAllEventListener()
    gladSdk.destroyAdSlots()
  })
}

/**
 * 지면별 중간광고 노출 간격
 * https://wiki.navercorp.com/pages/viewpage.action?pageId=1952794439
 **/
export const ADPosition = {
  /** 리뷰피드 */
  REVIEW_FEED: { FIRST: 2, INTERVAL: 4 }, // ex. 1번째 광고는 2개 이후 2번째 광고부터는 4개마다 노출

  /** 업체 리뷰엔드 - 이장소의다른리뷰 */
  REVIEW_PLACE_END: { FIRST: 1, INTERVAL: 4 },

  /** 리뷰탭 */
  REVIEW_TAB: { FIRST: 3, INTERVAL: 5 },

  /** 피드탭 */
  FEED_TAB: { FIRST: 2, INTERVAL: 5 },

  /** 타임라인탭 - 제거 필요 */
  VISIT_TAB: { FIRST: 4, INTERVAL: 4 },

  /** (신규) 타임라인 페이지 리스트 */
  TIMELINE_LIST: { FIRST: 4, INTERVAL: 4 },
} as const

export type AdPageType = keyof typeof ADPosition

/**
 * 중간광고 노출위치 여부
 * @param rowCount 줄단위 노출일경우 rowCount 반영
 */
export const isADPositionLine = (index: number, page: AdPageType, rowCount: number = 1) => {
  if (index < 0) return false

  const { FIRST, INTERVAL } = ADPosition[page] // 첫번째항이 FIRST, 공차가 INTERVAL인 수열

  return index % (rowCount * INTERVAL) === rowCount * FIRST - 1
}

/**
 * 광고슬롯 id 와 노출위치를 매핑
 * @param rowCount 줄단위 노출일경우 rowCount 반영
 */
export const getADElementIdMap = (itemsLength: number, page: AdPageType, rowCount: number = 1) => {
  if (!isMobile || !itemsLength) return {}

  const adElementIdMap: Record<string, string> = {}

  Array.from({ length: itemsLength }, (_, i) => i)
    .filter(position => isADPositionLine(position, page, rowCount))
    .forEach(position => {
      adElementIdMap[position] = `${page}_${position}`
    })

  return adElementIdMap
}

export const useAdEvent = (event: ValueOf<GladSdkEvent>, callback: GladSdkListener) => {
  const cbRef = useRef<GladSdkListener>(callback)
  cbRef.current = callback

  useEffect(() => {
    const listener = cbRef.current

    window.gladsdk?.cmd.push(() => {
      window.gladsdk?.addEventListener(event, listener)
    })

    return () => {
      window.gladsdk?.cmd.push(() => {
        window.gladsdk?.removeEventListener(event, listener)
      })
    }
  }, [event, callback])
}

interface AdHeight {
  minHeight: number | undefined
}

/**
 * @see https://oss.navercorp.com/place/local/issues/19624#issuecomment-15441346
 * 광고 타입별 최소/최대 높이값 적용
 */
export const getAdHeightByType = (type: AdType): AdHeight => {
  switch (type) {
    case AdType.SMART_CHANNEL:
      return { minHeight: 80 }
    case AdType.M_SUB:
      return { minHeight: 200 }
    default:
      return { minHeight: undefined }
  }
}
